import { useEffect, useState } from "react";
import axios from "axios";
import { Download, LogOut, Loader2, Lock, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aevum_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchSubscribers = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/newsletter`, {
        headers: { "X-Admin-Token": t },
      });
      setSubscribers(res.data);
      localStorage.setItem(TOKEN_KEY, t);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong admin token");
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      } else {
        toast.error("Could not load subscribers");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed && token) fetchSubscribers(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    fetchSubscribers(token.trim());
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setToken("");
    setSubscribers([]);
  };

  const onExport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/newsletter/export.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "aevum_subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  // ----- Login screen -----
  if (!authed) {
    return (
      <main
        data-testid="admin-login"
        className="min-h-screen bg-[#050505] text-[#F5F2EA] font-body flex items-center justify-center px-6"
      >
        <form
          onSubmit={onLogin}
          className="w-full max-w-md border border-white/10 bg-[#0a100d] p-10"
        >
          <div className="flex items-center gap-3 mb-8">
            <Lock size={16} className="text-[#155E63]" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#155E63] font-bold">
              Aevum / Admin
            </span>
          </div>
          <h1 className="font-display text-4xl text-[#F5F2EA] leading-tight mb-2">
            Restricted.
          </h1>
          <p className="text-sm text-[#A3A3A3] mb-8 leading-relaxed">
            Enter the admin token to view subscribers.
          </p>

          <label className="block text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">
            Admin token
          </label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="admin-token-input"
            className="w-full bg-transparent border-b border-white/20 focus:border-[#155E63] outline-none px-1 py-3 text-[#F5F2EA] placeholder:text-[#A3A3A3]/60"
            placeholder="••••••••••••••••"
            autoFocus
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-8 w-full bg-[#F5F2EA] text-[#050505] hover:bg-[#155E63] hover:text-[#F5F2EA] transition-all duration-500 px-6 py-4 text-[11px] tracking-[0.3em] uppercase font-bold"
          >
            Enter
          </button>
        </form>
      </main>
    );
  }

  // ----- Dashboard -----
  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#050505] text-[#F5F2EA] font-body"
    >
      <header className="border-b border-white/10 px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-display text-2xl tracking-[0.2em]">AEVUM</span>
          <span className="text-[11px] tracking-[0.3em] uppercase text-[#155E63] font-bold border-l border-white/10 pl-3 ml-1">
            Admin
          </span>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#F5F2EA] transition-colors"
        >
          <LogOut size={14} /> Logout
        </button>
      </header>

      <section className="px-6 sm:px-10 lg:px-16 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#155E63] font-bold mb-3">
              The Letter — Subscribers
            </p>
            <h1 className="font-display text-5xl sm:text-6xl text-[#F5F2EA] leading-none">
              {subscribers.length}{" "}
              <span className="italic text-[#F5F2EA]/60">souls.</span>
            </h1>
          </div>
          <button
            onClick={onExport}
            disabled={downloading || subscribers.length === 0}
            data-testid="admin-export-csv"
            className="inline-flex items-center justify-center gap-3 bg-[#F5F2EA] text-[#050505] hover:bg-[#155E63] hover:text-[#F5F2EA] transition-all duration-500 px-6 py-4 text-[11px] tracking-[0.3em] uppercase font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Exporting
              </>
            ) : (
              <>
                <Download size={14} />
                Export CSV
              </>
            )}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#A3A3A3] text-sm">
            <Loader2 size={16} className="animate-spin" /> Loading subscribers…
          </div>
        ) : subscribers.length === 0 ? (
          <div className="border border-white/10 p-16 text-center">
            <Users size={28} className="mx-auto text-[#155E63] mb-4" />
            <p className="font-display text-2xl text-[#F5F2EA]">
              Quiet for now.
            </p>
            <p className="text-sm text-[#A3A3A3] mt-2">
              Subscribers will appear here as they join.
            </p>
          </div>
        ) : (
          <div
            data-testid="subscribers-table"
            className="border border-white/10 overflow-hidden"
          >
            <div className="grid grid-cols-12 px-6 py-4 border-b border-white/10 text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3]">
              <div className="col-span-1">#</div>
              <div className="col-span-7">Email</div>
              <div className="col-span-4 text-right">Subscribed</div>
            </div>
            {subscribers.map((s, i) => (
              <div
                key={s.id}
                data-testid="subscriber-row"
                className="grid grid-cols-12 px-6 py-5 border-b border-white/5 hover:bg-white/[0.02] transition-colors items-center"
              >
                <div className="col-span-1 text-xs text-[#A3A3A3]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-7 text-[#F5F2EA] truncate font-mono text-sm">
                  {s.email}
                </div>
                <div className="col-span-4 text-right text-xs text-[#A3A3A3]">
                  {new Date(s.subscribed_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
